// Shared discovery navigation (desktop bar + compact menus). Photos/Videos are browse filters, Submit/Requests live in My Page.
export const NAV_BROWSE = [
  { l: "Latest", key: "latest", to: "/browse" },
  { l: "Top Rated", key: "top", to: "/browse?sort=top" },
  { l: "Trending", key: "trending", to: "/browse?sort=trending" },
];
export const NAV_ROUTES = [
  { l: "Map", to: "/map", testid: "nav-map", badge: "NEW", match: ["/map", "/place"] },
  { l: "Battles", to: "/battles", testid: "nav-battles", match: ["/battles", "/battle"] },
  { l: "Blind Rating", to: "/blind", testid: "nav-blind", match: ["/blind"] },
  { l: "Top Places", to: "/leaderboards", testid: "nav-leaderboards", badge: "NEW", match: ["/leaderboards"] },
  { l: "Swipe", to: "/swipe", testid: "nav-swipe", badge: "NEW", match: ["/swipe", "/rate-later"], mobileOnly: true }, // deck UX fits touch; not in the desktop bar
];
export const DESKTOP_NAV_ROUTES = NAV_ROUTES.filter((n) => !n.mobileOnly);
export const MY_PAGE_MATCH = ["/profile", "/my-uploads", "/requests", "/messages", "/admin", "/collection"];

const startsAny = (path, prefixes) => prefixes.some((p) => path === p || path.startsWith(p + "/") || path.startsWith(p + "?"));

// Resolve the current route to ONE nav section key (nested/detail routes map to their parent section).
export const activeSection = (pathname, search = "") => {
  if (pathname === "/browse" || pathname === "/") {
    const sort = new URLSearchParams(search).get("sort");
    return sort === "top" ? "top" : sort === "trending" ? "trending" : "latest";
  }
  const r = NAV_ROUTES.find((n) => startsAny(pathname, n.match));
  if (r) return r.to;
  if (startsAny(pathname, ["/inbox"])) return "inbox";
  if (startsAny(pathname, MY_PAGE_MATCH)) return "mypage";
  return null;
};

// ONE navigation model: every parent has a landing page (`to`) and optional children. Label click = navigate to `to`;
// chevron = expand/collapse children. No destination is duplicated — children are the existing routes only.
const EXPLORE_ITEMS = [...NAV_BROWSE, ...NAV_ROUTES.filter((n) => ["/map", "/leaderboards"].includes(n.to))];
const RATE_ITEMS = NAV_ROUTES.filter((n) => ["/battles", "/blind"].includes(n.to));
const RATE_ITEMS_MOBILE = NAV_ROUTES.filter((n) => ["/battles", "/blind", "/swipe"].includes(n.to));
const dashboardItems = (user) => [
  { l: "Overview", key: "dash-overview", to: "/profile", match: ["/profile"] },
  { l: "My Uploads", key: "dash-uploads", to: "/my-uploads", match: ["/my-uploads"] },
  { l: "Requests", key: "dash-requests", to: "/requests", match: ["/requests"] },
  { l: "Messages", key: "dash-messages", to: "/messages", match: ["/messages"] },
  ...(user?.is_admin ? [{ l: "Admin Studio", key: "dash-admin", to: "/admin", match: ["/admin"], matchSearch: (sp) => sp.get("tab") !== "uploads" }] : []),
];
export const navSections = (user, { mobile = false } = {}) => [
  { key: "explore", label: "Explore", to: "/browse", testid: "nav-explore", items: EXPLORE_ITEMS, matchKeys: ["latest", "top", "trending", "/map", "/leaderboards"] },
  { key: "galleries", label: "Galleries", to: "/galleries", testid: "nav-galleries", items: [], match: ["/galleries", "/gallery"] },
  { key: "rate", label: "Rate", to: "/battles", testid: "nav-rate", items: mobile ? RATE_ITEMS_MOBILE : RATE_ITEMS, matchKeys: ["/battles", "/blind", "/swipe"] },
  ...(user?.is_admin ? [{ key: "inbox", label: "Inbox", to: "/inbox", testid: "nav-inbox", items: INBOX_ITEMS, matchKeys: ["inbox"], match: ["/inbox"], emphasis: true, countKey: "inbox" }] : []),
  ...(user ? [{ key: "dashboard", label: user.is_admin ? "Dashboard" : "My Page", to: "/profile", testid: "nav-dashboard", items: dashboardItems(user), matchKeys: ["mypage"], emphasis: true }] : []),
];
// Inbox children = existing queues only (Inbox reason filters, community queue, processing) — no new pages.
const INBOX_ITEMS = [
  { l: "All", key: "inbox-all", to: "/inbox", matchExact: "/inbox", matchSearch: (sp) => !sp.get("reason") },
  { l: "Needs Title", key: "inbox-title", to: "/inbox?reason=title", matchExact: "/inbox", matchSearch: (sp) => sp.get("reason") === "title" },
  { l: "Needs Description", key: "inbox-description", to: "/inbox?reason=description", matchExact: "/inbox", matchSearch: (sp) => sp.get("reason") === "description" },
  { l: "Needs Tags", key: "inbox-tags", to: "/inbox?reason=tags", matchExact: "/inbox", matchSearch: (sp) => sp.get("reason") === "tags" },
  { l: "Needs Location", key: "inbox-location", to: "/inbox?reason=location", matchExact: "/inbox", matchSearch: (sp) => sp.get("reason") === "location" },
  { l: "Community Review & Processing", key: "inbox-community", to: "/admin?tab=uploads", match: ["/admin"], matchSearch: (sp) => sp.get("tab") === "uploads" },
];
export const itemActive = (item, active, pathname, search = "") => {
  const sp = new URLSearchParams(search);
  if (item.matchExact) return pathname === item.matchExact && (!item.matchSearch || item.matchSearch(sp));
  if (item.matchSearch) return (item.match ? startsAny(pathname, item.match) : false) && item.matchSearch(sp);
  return active === (item.key || item.to) || (item.match ? startsAny(pathname, item.match) : false);
};
export const sectionActive = (section, active, pathname, search = "") =>
  (section.matchKeys || []).includes(active) || (section.match ? startsAny(pathname, section.match) : false) || section.items.some((i) => itemActive(i, active, pathname, search));
export const itemTestId = (item) => item.key || (item.testid ? item.testid.replace("nav-", "") : item.to.replace(/\//g, ""));

// Remembered expanded sections (mobile menu). Active parent is always expanded on top of this.
const EXPANDED_KEY = "bf_nav_expanded";
export const loadExpanded = () => { try { return JSON.parse(localStorage.getItem(EXPANDED_KEY) || "[]"); } catch { return []; } };
export const saveExpanded = (keys) => { try { localStorage.setItem(EXPANDED_KEY, JSON.stringify(keys)); } catch { /* ignore */ } };

// Valid deep-link values for /browse?type= and ?sort= (anything else falls back to defaults)
export const parseBrowseParams = (search) => {
  const sp = new URLSearchParams(search);
  const type = ["photos", "videos"].includes(sp.get("type")) ? sp.get("type") : "all";
  const sort = ["top", "trending", "most"].includes(sp.get("sort")) ? sp.get("sort") : "latest";
  return { type, sort };
};
